import { ReactElement } from 'react';
import {
  Modal,
  FormTextField,
  ButtonTypes,
  TextFieldTypes,
  FormButton,
  ICertmasterPlatformCredential,
  sha256,
  IError,
  useToast,
} from '@comptia-sso/core';
import { useTranslation, Trans } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import { ChevronLeft } from '@material-ui/icons';

import { env } from 'config';

import styles from './CertMasterAuthenticateModal.module.scss';
import { useProfile } from '../../../hooks';
import { linkCertMasterAccount } from '../../../services';

import comptiaLogo from 'images/comptia-logo.png';

interface Props {
  isShowing: boolean;
  onClose: () => void;
  onLinked: () => void;
  onBackClicked?: () => void;
}

export const CertMasterAuthenticateModal = ({
  isShowing,
  onClose,
  onLinked,
  onBackClicked,
}: Props): ReactElement => {
  const [profile, { refetch }] = useProfile();
  const { t } = useTranslation();
  const [popToast] = useToast();

  const formik = useFormik({
    initialValues: {
      login: '',
      password: '',
    },
    onSubmit: async () => {
      try {
        const phash = await sha256(formik.values.password);
        const credential = {
          login: formik.values.login,
          phash,
        } as ICertmasterPlatformCredential;
        credential.email = profile.email;
        const response = await linkCertMasterAccount(profile.ssoId, credential);

        formik.setFieldValue('password', '');

        if (response === 'Success') {
          await refetch();
          onLinked();
        }
        formik.setErrors({
          login: t(`CertMasterAuthenticateModal.${response}`),
        });
      } catch (error) {
        // popToast((error as IError)?.message || t('Toast.Error.Default'));
        formik.setErrors({
          login: t(`CertMasterAuthenticateModal.LoginFailed`),
        });
      }
    },
    validateOnChange: false,
  });

  let backButton: JSX.Element | undefined;

  if (onBackClicked)
    backButton = (
      <button onClick={onBackClicked} className={styles.backButton}>
        <ChevronLeft fontSize="large" />
      </button>
    );

  const handleClose = () => {
    formik.setFieldValue('password', '');
    formik.setErrors({});
    onClose();
  };

  return (
    <Modal
      open={isShowing}
      onClose={handleClose}
      title={<img alt="CompTIA Logo" src={comptiaLogo} />}
      className={styles.modal}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      preTitle={backButton}
    >
      <p className={styles.instructions}>
        <Trans
          i18nKey="CertMasterAuthenticateModal.Instructions"
          components={{
            a: (
              <a
                href={env.COMPTIA_PLATFORM_HOST}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </p>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <FormTextField
            label={t('CertMasterAuthenticateModal.UsernameLabel')}
            name="login"
            error={formik.errors.login}
            className={styles.usernameField}
            value={formik.values.login}
            onChange={formik.handleChange}
            required
            type={TextFieldTypes.Text}
          />
          <FormTextField
            label={t('CertMasterAuthenticateModal.PasswordLabel')}
            className={styles.passwordField}
            name="password"
            type={TextFieldTypes.Password}
            onChange={formik.handleChange}
            required
          />

          <FormButton
            className="certmaster-modal-login-btn"
            type={ButtonTypes.Submit}
          >
            {t('CertMasterAuthenticateModal.LoginBtn')}
          </FormButton>
        </form>
      </FormikProvider>
      <div className={styles.links}>
        <a
          href={`${env.COMPTIA_PLATFORM_HOST}?linking-forgotpassword=true`}
          target="_blank"
          rel="noopener noreferrer"
          className="certmaster-link"
        >
          {t('CertMasterAuthenticateModal.ForgotLink')}
        </a>

        <a
          data-i18next="SignUp.Disclaimer.ContactUs"
          href="https://help.comptia.org"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('CertMasterAuthenticateModal.SupportLink')}
        </a>
      </div>
    </Modal>
  );
};
